// ============================================================
// usePersistedToggle.ts
// Reads a boolean layout preference off the authenticated user's
// viewingPaneToggles map (backend User.viewingPaneToggles), with a
// hardcoded default fallback for pre-first-write users.
//
// Usage:
//   const [collapsed, setCollapsed] = usePersistedToggle('sidebar.collapsed', false)
//
// The setter mutates the store slot IMMEDIATELY (optimistic) and
// schedules a debounced PATCH /api/auth/me via the existing
// scheduleTogglesSave singleton in useCasevoStore (2 s window —
// several clicks in a row collapse into one PATCH).
//
// Failure mode: PATCH errors are swallowed with a single
// console.warn in scheduleTogglesSave; the toggle keeps working
// locally exactly as it did before persistence existed. No
// user-facing error banner for a layout preference.
//
// KEY SHAPE: prefer dot-namespaced (e.g. `sidebar.collapsed`,
// `case.injuryDetails.collapsed`) — the backend F-17 validator
// (authController.updateMe) accepts both this shape and the legacy
// single-word case-heading keys, but new callers should stay in
// the namespaced style so the map's contents self-document.
// ============================================================
import { useCallback } from 'react'
import { useCasevoStore } from '../store/useCasevoStore'

export function usePersistedToggle(
  key: string,
  defaultValue: boolean,
): [boolean, (next: boolean) => void] {
  // Subscribe to the specific key so the component re-renders only
  // when THIS key flips — not when any other toggle changes. The
  // Zustand selector's shallow equality on the returned boolean
  // gives cheap per-key subscription without the equalityFn dance.
  const value = useCasevoStore((s) => {
    const v = s.viewingPaneToggles[key]
    return typeof v === 'boolean' ? v : defaultValue
  })

  // Stable setter — closes over `key` only. setToggle is a stable
  // action reference from the store, so useCallback with [key]
  // gives a fresh callback per key without churning on every render.
  const setValue = useCallback(
    (next: boolean) => {
      useCasevoStore.getState().setToggle(key, next)
    },
    [key],
  )

  return [value, setValue]
}
